/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { uploadSupportedDocument } from 'queries/ossix/user/userData';
import { IKycDocumentState } from 'utils/kycStatus';
import {
  buildKycUploadPayload,
  fileExtension,
  uploadToFileService,
} from 'utils/uploadToFileService';

export const MAX_KYC_FILE_MB = 5;

export type StepStatus = 'idle' | 'uploading' | 'error';

type Draft = { file: File; url: string };

/**
 * Pick -> preview -> confirm for KYC documents. A picked file gets a local
 * preview first; only "confirm" sends it (file service, then UploadKYCDocs).
 * The local preview is kept after a successful upload so the user keeps
 * seeing their photo while the platform's copy propagates.
 */
export default ({
  onInvalid,
  onUploaded,
}: {
  onInvalid: (reason: 'type' | 'size') => void;
  onUploaded: (doc: IKycDocumentState) => void;
}) => {
  const { mutate: saveKycDocument } = uploadSupportedDocument();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [statuses, setStatuses] = useState<
    Record<string, StepStatus>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadedPreviews, setUploadedPreviews] = useState<
    Record<string, string>
  >({});
  const objectUrls = useRef<string[]>([]);

  useEffect(
    () => () =>
      objectUrls.current.forEach((url) => URL.revokeObjectURL(url)),
    [],
  );

  const setStatus = (key: string, status: StepStatus) =>
    setStatuses((current) => ({ ...current, [key]: status }));

  const setError = (key: string, message: string) =>
    setErrors((current) => ({ ...current, [key]: message }));

  const onPick = (doc: IKycDocumentState, file: File) => {
    if (!/^image\/(png|jpeg)$/.test(file.type)) {
      onInvalid('type');
      return;
    }
    if (file.size / 1024 / 1024 > MAX_KYC_FILE_MB) {
      onInvalid('size');
      return;
    }
    const url = URL.createObjectURL(file);
    objectUrls.current.push(url);
    setDrafts((current) => ({
      ...current,
      [doc.key]: { file, url },
    }));
    setStatus(doc.key, 'idle');
  };

  const onDiscard = (doc: IKycDocumentState) =>
    setDrafts(({ [doc.key]: _removed, ...rest }) => rest);

  const onConfirm = async (doc: IKycDocumentState) => {
    const draft = drafts[doc.key];
    if (!draft) return;
    setStatus(doc.key, 'uploading');
    setError(doc.key, '');

    const fail = (message?: string) => {
      setStatus(doc.key, 'error');
      setError(doc.key, message || '');
    };

    try {
      const url = await uploadToFileService(draft.file);
      saveKycDocument(
        buildKycUploadPayload({
          PID: localStorage.getItem('userPID'),
          url,
          type: doc.kycType,
          extension: fileExtension(draft.file.name),
        }),
        {
          onSuccess: () => {
            setUploadedPreviews((current) => ({
              ...current,
              [doc.key]: draft.url,
            }));
            onDiscard(doc);
            setStatus(doc.key, 'idle');
            onUploaded(doc);
          },
          onError: (error: any) =>
            fail(error?.response?.data?.[0]?.Description),
        },
      );
    } catch {
      fail();
    }
  };

  return {
    drafts,
    statuses,
    errors,
    uploadedPreviews,
    onPick,
    onDiscard,
    onConfirm,
  };
};
